import React from "react";
import { Navigate, Route } from "react-router-dom";
import AdminLayout from "./AdminLayout";

// Lazy-load all admin sub-pages for code splitting
const Dashboard = React.lazy(() => import("./Dashboard"));
const Users = React.lazy(() => import("./Users"));
const UsersUnvalidated = React.lazy(() => import("./UsersUnvalidated"));
const Subclans = React.lazy(() => import("./Subclans"));
const Events = React.lazy(() => import("./Events"));
const Settings = React.lazy(() => import("./Settings"));
// ProfileFields admin page was removed when the parallel ProfileFieldSchema
// registry was collapsed into PlatformSettings.userFields. User fields are
// now edited via Platform Settings → User Fields.
const Pages = React.lazy(() => import("./Pages"));
const MenuBuilder = React.lazy(() => import("./MenuBuilder"));
const Domains = React.lazy(() => import("./Domains"));
const Notifications = React.lazy(() => import("./Notifications"));
const ModerationQueue = React.lazy(() => import("./ModerationQueue"));
const AuditTrail = React.lazy(() => import("./AuditTrail"));

// Suspense wrapper for lazy-loaded admin pages
function AdminSuspense({ children }) {
  return (
    <React.Suspense
      fallback={
        <div
          style={{
            textAlign: "center",
            padding: 40,
            color: "#888",
            fontFamily: '"Roboto Slab", serif',
            fontSize: 14,
          }}
        >
          Loading...
        </div>
      }
    >
      {children}
    </React.Suspense>
  );
}

/**
 * Generates the nested <Route> elements for the /administrator path.
 * To be used inside the main <Routes> component:
 *
 *   <Route path="administrator" element={<AdminLayout />}>
 *     {adminRoutes}
 *   </Route>
 *
 * Or import getAdminRouteElement() for the full <Route> tree.
 */
export const adminRoutes = (
  <>
    <Route
      index
      element={
        <AdminSuspense>
          <Dashboard />
        </AdminSuspense>
      }
    />
    <Route
      path="users"
      element={
        <AdminSuspense>
          <Users />
        </AdminSuspense>
      }
    />
    <Route
      path="users/unvalidated"
      element={
        <AdminSuspense>
          <UsersUnvalidated />
        </AdminSuspense>
      }
    />
    <Route
      path="subclans"
      element={
        <AdminSuspense>
          <Subclans />
        </AdminSuspense>
      }
    />
    <Route
      path="events"
      element={
        <AdminSuspense>
          <Events />
        </AdminSuspense>
      }
    />
    {/* Content merged into Moderation's "Reports" tab (prototype:
        04-moderation-events.jsx — "Moderation (merged Content+Moderation)").
        Content.jsx was a pure prop-preset wrapper around ModerationQueue with
        no unique capability (see ModerationQueue.jsx's initialFlagFilter
        handling) — redirect rather than delete the route so any bookmark to
        /administrator/content still lands somewhere useful. This is the
        same "redirect, don't delete" house style the "domains" path below
        used to follow too, before it was promoted to a real route
        (.agent/clan-admin-parity-execplan.md's Domains stage) — kept here
        as precedent even though that specific example no longer applies. */}
    <Route
      path="content"
      element={<Navigate to="../moderation?tab=reports" replace />}
    />
    <Route
      path="settings"
      element={
        <AdminSuspense>
          <Settings />
        </AdminSuspense>
      }
    />
    {/* /admin/profilefields route removed: user fields are now edited
        in Platform Settings → User Fields. */}
    <Route
      path="pages"
      element={
        <AdminSuspense>
          <Pages />
        </AdminSuspense>
      }
    />
    <Route
      path="menu-builder"
      element={
        <AdminSuspense>
          <MenuBuilder />
        </AdminSuspense>
      }
    />
    {/* Domains promoted to a real, flat route — a peer of "pages" and
        "menu-builder" above, matching the prototype's Public-site model
        (a flat panel, no "Kits" grouping concept at all — see
        <SCRATCH>/reports/N9-settings-dev-publicsite.md). This used to
        redirect to "../settings?tab=events&kit=domains" (Settings.jsx's
        "Kits" tab, 2-card grid, opening a dialog that embedded
        <Domains embedded/>); that URL is a bookmarked, working entry
        point in its own right and is deliberately left resolving exactly
        as before — Settings.jsx (the "Kits" grid + dialog),
        AdminSettingsTabs.jsx (the domains->events TAB_ALIASES entry) and
        settingsHubRegistry.js (the hub card's tab:"domains" target) are
        untouched by this route change, so nothing needed a compat
        redirect added here. Two working paths to the same panel is the
        deliberate interim state; see the Domains stage's W7 report for
        what a later wave should still do on the Settings side. */}
    <Route
      path="domains"
      element={
        <AdminSuspense>
          <Domains />
        </AdminSuspense>
      }
    />
    <Route
      path="notifications"
      element={
        <AdminSuspense>
          <Notifications />
        </AdminSuspense>
      }
    />
    <Route
      path="moderation"
      element={
        <AdminSuspense>
          <ModerationQueue />
        </AdminSuspense>
      }
    />
    <Route
      path="audit-trail"
      element={
        <AdminSuspense>
          <AuditTrail />
        </AdminSuspense>
      }
    />
  </>
);

/**
 * Returns the complete admin Route element with layout and nested routes.
 * Can be dropped directly into a <Routes> block.
 */
export function getAdminRouteElement() {
  return (
    <Route path="administrator" element={<AdminLayout />}>
      {adminRoutes}
    </Route>
  );
}

export { AdminLayout };
export default AdminLayout;
